import type { RouteParams } from 'vue-router';
import type { EntitlementChecker, VisibilityContext } from '#layers/access/public';
import { CONTENT_ROUTE_NAME, WORKSPACE_SETTINGS_ROUTE_NAME } from '#layers/spaces/public';

/** The slice of route meta the entitlement gate inspects. */
export interface GatedRouteMeta {
	isVisible?: (context: VisibilityContext) => boolean;
}

/** A matched route record reduced to what the gate needs: its name and gating meta. */
export interface MatchedRoute {
	name: string | undefined;
	meta: GatedRouteMeta;
}

/** Everything the redirect decision needs: the resolved target plus the entitlement state. */
export interface EntitlementRedirectInput {
	/** Whether the workspace's entitlements have loaded; while false a gated route fails closed. */
	ready: boolean;
	/** The target route's matched records, outermost first. */
	matched: MatchedRoute[];
	can: EntitlementChecker;
	/** The target route's resolved params, passed to param-aware `isVisible` gates for per-id checks. */
	params?: RouteParams;
	/** Organization entitlement checker, for gates on org-level pages (workspace edit/delete). */
	canOrganization?: EntitlementChecker;
	/** The current `:workspace` route param, carried onto any redirect. */
	workspaceParam: string;
	/** Lazily resolves the first settings route the user may see; only called for a blocked settings route. */
	resolveFirstVisibleSettingsRoute: () => string | undefined;
}

/** A vue-router redirect target. */
export interface EntitlementRedirect {
	name: string;
	params: { workspace: string };
}

/**
 * A route is allowed when it declares no `isVisible` gate, or its gate returns true for `can`.
 * Admin bypasses because the checker returns true for every entitlement. `params` are forwarded to
 * the gate for per-id checks; nav consumers omit them, leaving a scoped gate as an any-id check.
 */
export function shouldAllow(meta: GatedRouteMeta | undefined, can: EntitlementChecker, params?: RouteParams, canOrganization?: EntitlementChecker): boolean {
	if (!meta?.isVisible) return true;
	return meta.isVisible({ can, params, canOrganization });
}

/**
 * Decides where to send a navigation the user may not be entitled to reach; undefined allows it.
 * Until entitlements load, a gated route fails closed (redirect to the safe ungated content
 * landing) while an ungated route is allowed through. Once loaded, a blocked settings route
 * redirects to the first visible settings route; anything else — and settings with nothing
 * visible — falls back to the content landing. The leaf-name guard prevents a redirect loop.
 */
export function decideEntitlementRedirect(input: EntitlementRedirectInput): EntitlementRedirect | undefined {
	const redirectParams = { workspace: input.workspaceParam };

	if (!input.ready) {
		return input.matched.some((record) => Boolean(record.meta.isVisible))
			? { name: CONTENT_ROUTE_NAME, params: redirectParams }
			: undefined;
	}

	if (input.matched.every((record) => shouldAllow(record.meta, input.can, input.params, input.canOrganization))) return undefined;

	const leafName = input.matched.at(-1)?.name;
	const onSettingsRoute = input.matched.some((record) => record.name === WORKSPACE_SETTINGS_ROUTE_NAME);

	if (onSettingsRoute) {
		const target = input.resolveFirstVisibleSettingsRoute();
		if (target && target !== leafName) return { name: target, params: redirectParams };
	}

	return { name: CONTENT_ROUTE_NAME, params: redirectParams };
}
